import { IconStar, IconArrowRight } from '../icons'

/**
 * PartyTrilogyStandings
 *
 * Between-round standings screen shown when party.status === 'round_standings'.
 * Used by both the host screen (PartyTrilogyGameplay, isHost=true)
 * and player phones (PartyPlayerScreen, isHost=false).
 *
 * Leaderboard is sorted by CUMULATIVE hops ascending, cumulative time as tiebreaker.
 * The player(s) who achieved the minimum hops this round get a gold "Fastest" badge.
 *
 * Props
 *   party         — live Firestore party doc
 *   isHost        — true: show Next Round / Final Results button
 *                   false: show "Waiting for host..."
 *   onNextRound   — host: start next round (rounds 1-2)
 *   onFinalResults— host: advance to final standings (round 3)
 */

function fmtMs(ms) {
  const s = Math.round((ms ?? 0) / 1000)
  const m = String(Math.floor(s / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return `${m}:${sec}`
}

/**
 * Themed numbered rank chip — gold/silver/bronze gradient for top 3, plain
 * "#N" muted for everything else. These tier colors are intentionally
 * theme-stable (universal podium colors, like the difficulty pills).
 */
function RankChip({ place }) {
  if (place === 1) return <span className="trilogy-rank-chip trilogy-rank-chip--gold">1</span>
  if (place === 2) return <span className="trilogy-rank-chip trilogy-rank-chip--silver">2</span>
  if (place === 3) return <span className="trilogy-rank-chip trilogy-rank-chip--bronze">3</span>
  return <span className="trilogy-rank-chip trilogy-rank-chip--plain">#{place}</span>
}

export default function PartyTrilogyStandings({ party, isHost, onNextRound, onFinalResults, onBackToLobby }) {
  const currentRound = party?.currentRound ?? 1
  const isLastRound  = currentRound >= 3
  const players      = party?.players ?? {}
  const roundResults = party?.rounds?.[currentRound]?.results ?? {}
  const cumulative   = party?.cumulativeScores ?? {}
  // round_standings should only ever be reached with a finalized results map. If
  // it's empty (a transient/partial write mid round-advance), don't render every
  // player as DNF — show a brief finalizing state instead.
  const resultsReady = Object.keys(roundResults).length > 0

  // Round-winner detection — any player who finished with the fewest hops this round
  const completedHops = Object.values(roundResults)
    .filter((r) => r.completed)
    .map((r) => r.hops)
  const minHops = completedHops.length > 0 ? Math.min(...completedHops) : null
  // Tie-break: the Fastest badge only appears when exactly ONE player holds the
  // lowest hop count this round. A tie for fastest shows it to nobody.
  const fastestCount = minHops !== null ? completedHops.filter((h) => h === minHops).length : 0

  // Sort by cumulative total hops (asc), cumulative time (asc) as tiebreaker
  const sorted = Object.entries(players).sort(([aUid], [bUid]) => {
    const a = cumulative[aUid] ?? { totalHops: 999, totalTimeMs: 99_999_999 }
    const b = cumulative[bUid] ?? { totalHops: 999, totalTimeMs: 99_999_999 }
    if (a.totalHops !== b.totalHops) return a.totalHops - b.totalHops
    return a.totalTimeMs - b.totalTimeMs
  })

  // Puzzle label — stored on top-level party doc while standings are shown
  const puzzleLabel =
    party?.startActor && party?.goalActor
      ? `${party.startActor.name} → ${party.goalActor.name}`
      : null

  if (!resultsReady) {
    return (
      <div className="trilogy-standings">
        <p className="trilogy-standings__eyebrow">Round {currentRound} of 3</p>
        <h2 className="trilogy-standings__title">Round {currentRound} Complete</h2>
        <p className="trilogy-standings__waiting">Finalizing round…</p>
      </div>
    )
  }

  return (
    <div className="trilogy-standings">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <p className="trilogy-standings__eyebrow">Round {currentRound} of 3</p>
      <h2 className="trilogy-standings__title">Round {currentRound} Complete</h2>
      {puzzleLabel && (
        <p className="trilogy-standings__puzzle">{puzzleLabel}</p>
      )}

      {/* ── Column headers ──────────────────────────────────────────────────── */}
      <div className="trilogy-standings__col-headers">
        <span className="trilogy-standings__col-rank" />
        <span className="trilogy-standings__col-avatar" />
        <span className="trilogy-standings__col-name">Player</span>
        <span className="trilogy-standings__col-round">Round</span>
        <span className="trilogy-standings__col-total">Total</span>
        <span className="trilogy-standings__col-time">Time</span>
      </div>

      {/* ── Player rows ─────────────────────────────────────────────────────── */}
      <div className="trilogy-standings__table">
        {sorted.map(([uid, p], i) => {
          const res = roundResults[uid]
          const cum = cumulative[uid]
          const isRoundBest    = res?.completed && minHops !== null && res.hops === minHops && fastestCount === 1
          const isOverallLead  = i === 0
          const initial = (p.username || 'G').charAt(0).toUpperCase()

          return (
            <div
              key={uid}
              className={[
                'trilogy-standings__row',
                isOverallLead  ? 'trilogy-standings__row--leader'     : '',
                isRoundBest && !isOverallLead ? 'trilogy-standings__row--round-best' : '',
              ].filter(Boolean).join(' ')}
            >
              {/* Rank chip (gold/silver/bronze/plain) */}
              <span className="trilogy-standings__rank">
                <RankChip place={i + 1} />
              </span>

              {/* Avatar */}
              <div className="trilogy-standings__avatar">
                {p.avatar
                  ? <img src={p.avatar} alt={p.username} />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>

              {/* Name + optional round-best badge */}
              <div className="trilogy-standings__name-col">
                <span className="trilogy-standings__name">{p.username || 'Guest'}</span>
                {isRoundBest && (
                  <span className="trilogy-standings__round-badge">
                    <IconStar size={12} aria-hidden="true" />
                    <span>Fastest</span>
                  </span>
                )}
              </div>

              {/* Hops this round */}
              {res?.completed ? (
                <span className="trilogy-standings__this-round">
                  {res.hops} hop{res.hops !== 1 ? 's' : ''}
                </span>
              ) : (
                <span className="trilogy-standings__this-round trilogy-standings__this-round--dnf">
                  DNF
                </span>
              )}

              {/* Cumulative hops */}
              <span className="trilogy-standings__total">
                {cum?.totalHops ?? '—'}
              </span>

              {/* Cumulative time */}
              <span className="trilogy-standings__total-time">
                {cum ? fmtMs(cum.totalTimeMs) : '--:--'}
              </span>
            </div>
          )
        })}
      </div>

      {/* ── Actions ─────────────────────────────────────────────────────────── */}
      {isHost ? (
        <div className="trilogy-standings__actions">
          {isLastRound ? (
            <button
              className="btn-primary trilogy-standings__action-btn"
              onClick={onFinalResults}
            >
              <span>Final Results</span>
              <IconArrowRight size={18} aria-hidden="true" />
            </button>
          ) : (
            <button
              className="btn-primary trilogy-standings__action-btn"
              onClick={onNextRound}
            >
              <span>Round {currentRound + 1} · Start</span>
              <IconArrowRight size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="trilogy-standings__waiting">Waiting for host...</p>
          <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby} style={{ marginTop: 16 }}>
            Back to Lobby
          </button>
        </>
      )}
    </div>
  )
}
